import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { Typography, Spinner } from '@material-tailwind/react';

const UserMentionProfile = () => {
    const { rootUrl } = require('../../../config/config');
    const { username } = useParams();
    const [token] = useState(localStorage.getItem('authToken') || '');
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        let isMounted = true;

        const fetchUser = async () => {
            setLoading(true);
            setError('');

            try {
                const response = await axios.get(
                    `${rootUrl}/api/users/lookup/${encodeURIComponent(username)}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );

                if (isMounted && response.data.success) {
                    setUser(response.data.data);
                } else if (isMounted) {
                    setError(response.data.message || 'Failed to load user');
                }
            } catch (requestError) {
                if (isMounted) {
                    setError(requestError?.response?.data?.message || 'Failed to load user');
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        fetchUser();

        return () => {
            isMounted = false;
        };
    }, [rootUrl, token, username]);

    const getInitials = (nameValue) => {
        if (!nameValue) return 'U';
        return nameValue.split(' ').map((part) => part[0]).join('').toUpperCase().slice(0, 2);
    };

    return (
        <div className="min-h-full px-4 py-6">
            <div className="mx-auto max-w-2xl rounded-3xl border border-white/60 bg-white/90 p-6 shadow-[0_20px_80px_rgba(15,23,42,0.08)] backdrop-blur">
                <div className="mb-6 flex items-center justify-between gap-3">
                    <div>
                        <Typography variant="h4" className="text-blue-gray-900">
                            Mentioned staff
                        </Typography>
                        <Typography variant="small" className="text-blue-gray-500">
                            View the staff member referenced in a task comment.
                        </Typography>
                    </div>
                    <Link
                        to="/workflows"
                        className="rounded-full border border-[#FF5733]/20 px-4 py-2 text-sm font-semibold text-[#FF5733] transition-colors hover:bg-[#FF5733]/5"
                    >
                        Back to workflows
                    </Link>
                </div>

                {loading ? (
                    <div className="flex justify-center py-10">
                        <Spinner className="h-8 w-8" color="blue" />
                    </div>
                ) : error ? (
                    <div className="rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
                        {error}
                    </div>
                ) : user ? (
                    <div className="flex flex-col gap-6 rounded-3xl border border-blue-gray-50 bg-blue-gray-50/60 p-5 md:flex-row md:items-center">
                        <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-3xl bg-[#FF5733] text-2xl font-bold text-white shadow-lg shadow-orange-200/60">
                            {user.profile_picture ? (
                                <img
                                    src={`${rootUrl}${user.profile_picture}`}
                                    alt={user.name || user.username}
                                    className="h-full w-full rounded-3xl object-cover"
                                />
                            ) : (
                                getInitials(user.name || user.username)
                            )}
                        </div>

                        <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                                <Typography variant="h5" className="text-blue-gray-900">
                                    {user.name || user.username}
                                </Typography>
                                <span className="rounded-full bg-[#FF5733]/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[#FF5733]">
                                    {user.actor_type === 'staff' ? 'Staff' : user.actor_type || 'User'}
                                </span>
                            </div>
                            <Typography variant="small" className="text-blue-gray-600">
                                @{user.username}
                            </Typography>
                            <div className="mt-4 grid gap-3 sm:grid-cols-2">
                                <div className="rounded-2xl bg-white px-4 py-3 shadow-sm">
                                    <div className="text-xs font-semibold uppercase tracking-wide text-blue-gray-400">Status</div>
                                    <div className="mt-1 text-sm font-semibold text-blue-gray-800">
                                        {user.status || 'active'}
                                    </div>
                                </div>
                                <div className="rounded-2xl bg-white px-4 py-3 shadow-sm">
                                    <div className="text-xs font-semibold uppercase tracking-wide text-blue-gray-400">Username</div>
                                    <div className="mt-1 text-sm font-semibold text-blue-gray-800">
                                        @{user.username}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="rounded-2xl border border-blue-gray-100 bg-blue-gray-50 px-4 py-3 text-sm text-blue-gray-700">
                        No user record was returned.
                    </div>
                )}
            </div>
        </div>
    );
};

export default UserMentionProfile;
